import React, { useState } from 'react';
import { toast } from 'sonner';
import { Download, Plus, Receipt, Search, Upload, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SalesPager, SalesTableSkeleton } from '../components/sales/DailySalesTable';
import { queryString, useAbortableGet, useDebouncedValue } from '../components/sales/useAbortableGet';
import { PurchaseFormDialog } from '../components/purchases/PurchaseFormDialog';
import { PurchaseUploadDialog } from '../components/purchases/PurchaseUploadDialog';
import { PurchaseDetailDialog } from '../components/purchases/PurchaseDetailDialog';
import { StatusBadge, inr } from '../components/purchases/PurchaseBits';
import { downloadFile } from '../lib/download';
import { LoadError, errorMessage } from '../components/LoadError';
import { canDoAdminAction, canEditModule, viewOnlyHint } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GRID = '110px 150px minmax(200px, 1fr) 80px 90px 140px 110px';
const EMPTY = { search: '', supplier: 'ALL', status: 'ALL', date_from: '', date_to: '' };

const Stat = ({ label, value, testId }) => (
  <Card className="rounded-2xl border-0 bg-white p-4 shadow-sm"><p className="text-[12px] text-gray-500">{label}</p><p className="mt-1 text-[22px] font-semibold" data-testid={testId}>{value}</p></Card>
);

const Filters = ({ filters, setFilters, suppliers }) => {
  const set = key => value => setFilters({ ...filters, [key]: value });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="purchase-filters">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <Input value={filters.search} onChange={e => set('search')(e.target.value)} placeholder="Invoice no., SKU or supplier" aria-label="Search purchases" className="h-9 w-[240px] pl-8 text-[13px]" data-testid="purchase-search" />
      </div>
      <Select value={filters.supplier} onValueChange={set('supplier')}>
        <SelectTrigger className="h-9 w-[200px] text-[13px]" data-testid="purchase-supplier-filter"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="ALL">All suppliers</SelectItem>{suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={filters.status} onValueChange={set('status')}>
        <SelectTrigger className="h-9 w-[140px] text-[13px]" data-testid="purchase-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent>{['ALL', 'DRAFT', 'POSTED', 'REVERSED'].map(s => <SelectItem key={s} value={s} data-testid={`purchase-status-option-${s}`}>{s === 'ALL' ? 'All statuses' : s[0] + s.slice(1).toLowerCase()}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" value={filters.date_from} onChange={e => set('date_from')(e.target.value)} aria-label="Invoice date from" className="h-9 w-[150px] text-[13px]" data-testid="purchase-date-from" />
      <span className="text-gray-500" aria-hidden="true">–</span>
      <Input type="date" value={filters.date_to} onChange={e => set('date_to')(e.target.value)} aria-label="Invoice date to" className="h-9 w-[150px] text-[13px]" data-testid="purchase-date-to" />
      {JSON.stringify(filters) !== JSON.stringify(EMPTY) && <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY)} data-testid="purchase-clear-filters"><X className="mr-1 h-3.5 w-3.5" />Clear</Button>}
    </div>
  );
};

const PurchaseRow = ({ row, index, onOpen }) => (
  <button type="button" onClick={() => onOpen(row.id)} className="grid w-full items-center border-b text-left text-[13px] transition-colors hover:bg-gray-50" style={{ gridTemplateColumns: GRID }} data-testid={`purchase-row-${index}`}>
    <span className="p-4 text-gray-600">{row.invoice_date}</span>
    <span className="p-4 font-mono font-medium">{row.invoice_no}</span>
    <span className="truncate p-4">{row.supplier}</span>
    <span className="p-4 text-right">{row.lines?.length || 0}</span>
    <span className="p-4 text-right font-mono">{row.totals?.qty || 0}</span>
    <span className="p-4 text-right font-mono font-semibold">{inr(row.totals?.amount)}</span>
    <span className="p-4"><StatusBadge status={row.status} /></span>
  </button>
);

export default function PurchasesPage() {
  const { country } = useCountry();
  const { user } = useAuth();
  const isAdmin = canDoAdminAction(user, 'purchase_post'); // post, reverse and confirm upload are admin-only
  const canEdit = canEditModule(user, 'purchases');
  const [filters, setFiltersState] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [refreshKey, setRefreshKey] = useState(0);
  const [formInvoice, setFormInvoice] = useState(undefined);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const search = useDebouncedValue(filters.search.trim(), 350);
  const filterQuery = queryString({ ...filters, search, country });
  const { data, loading, error } = useAbortableGet(`${API}/purchases?${filterQuery}&page=${page}&page_size=${pageSize}`, refreshKey);
  const suppliers = useAbortableGet(`${API}/purchases/suppliers?country=${country}`, refreshKey).data?.suppliers || [];
  const refresh = () => setRefreshKey(k => k + 1);
  const setFilters = next => { setFiltersState(next); setPage(1); };
  const rows = error ? [] : (data?.rows || []);
  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY);
  const kpi = value => (error ? '—' : value);

  const exportList = () => downloadFile(`${API}/purchases/export?${filterQuery}`, 'PURCHASES_EXPORT.xlsx').catch(err => toast.error(`Export failed: ${errorMessage(err)}`));
  const openExisting = id => { setFormInvoice(undefined); setDetailId(id); };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Purchases</h1>
          <p className="mt-1 text-[15px] text-gray-500">Supplier invoices · posting adds stock and sets the latest purchase cost</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="h-9 text-[13px]" onClick={exportList} data-testid="purchase-export"><Download className="mr-2 h-4 w-4" />Export</Button>
          {canEdit && <Button variant="outline" className="h-9 text-[13px]" onClick={() => setUploadOpen(true)} data-testid="purchase-upload-button"><Upload className="mr-2 h-4 w-4" />Upload purchases</Button>}
          <Button className="h-9 bg-[#007AFF] text-[13px] text-white hover:bg-[#0066CC]" disabled={!canEdit} title={canEdit ? undefined : viewOnlyHint(user)} onClick={() => setFormInvoice(null)} data-testid="new-purchase-button"><Plus className="mr-2 h-4 w-4" />New purchase</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label={data?.totals_scope === 'POSTED' ? 'Posted invoices' : 'Invoices'} value={kpi((data?.totals?.invoices ?? 0).toLocaleString('en-IN'))} testId="purchase-total-invoices" />
        <Stat label={data?.totals_scope === 'POSTED' ? 'Posted quantity' : 'Quantity'} value={kpi((data?.totals?.qty ?? 0).toLocaleString('en-IN'))} testId="purchase-total-qty" />
        <Stat label={data?.totals_scope === 'POSTED' ? 'Posted amount (excl. GST)' : 'Amount (excl. GST)'} value={kpi(inr(data?.totals?.amount))} testId="purchase-total-amount" />
      </div>
      <Card className="rounded-2xl border-0 bg-white p-4 shadow-sm"><Filters filters={filters} setFilters={setFilters} suppliers={suppliers} /></Card>
      <Card className="overflow-hidden rounded-2xl border-0 bg-white shadow-sm">
        <div className="overflow-x-auto" data-testid="purchase-table">
          <div className="min-w-[880px]">
            <div className="grid bg-gray-50 text-[12px] font-semibold uppercase text-gray-500" style={{ gridTemplateColumns: GRID }}>
              {['Date', 'Invoice', 'Supplier', 'Lines', 'Qty', 'Amount', 'Status'].map((h, i) => <span key={h} className={`p-4 ${i >= 3 && i <= 5 ? 'text-right' : ''}`}>{h}</span>)}
            </div>
            {error && !loading && <LoadError what="purchases" error={error} onRetry={refresh} className="m-4" testId="purchase-load-error" />}
            {loading && (!data || error) ? <SalesTableSkeleton rows={6} /> : rows.map((row, i) => <PurchaseRow key={row.id} row={row} index={i} onOpen={setDetailId} />)}
            {!loading && !error && rows.length === 0 && (hasFilters ? (
              <div className="py-12 text-center text-[14px] text-gray-600" data-testid="purchase-empty-filtered">
                <p>No invoices match these filters.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setFilters(EMPTY)} data-testid="purchase-empty-clear-filters"><X className="mr-1 h-3.5 w-3.5" />Clear filters</Button>
              </div>
            ) : (
              <div className="flex flex-col items-center py-12 text-center text-[14px] text-gray-600" data-testid="purchase-empty">
                <Receipt className="mb-2 h-8 w-8 text-gray-400" aria-hidden="true" />
                <p>No purchases yet. Record a supplier invoice with New purchase.</p>
                {canEdit && <Button size="sm" className="mt-3 bg-[#007AFF] text-white hover:bg-[#0066CC]" onClick={() => setFormInvoice(null)} data-testid="purchase-empty-new"><Plus className="mr-1 h-4 w-4" />New purchase</Button>}
              </div>
            ))}
          </div>
        </div>
        <SalesPager prefix="purchase" page={page} pageSize={pageSize} total={error ? 0 : (data?.total || 0)} loading={loading} onPage={setPage} onPageSize={size => { setPageSize(size); setPage(1); }} />
      </Card>
      <PurchaseFormDialog open={formInvoice !== undefined} onOpenChange={open => !open && setFormInvoice(undefined)} invoice={formInvoice} country={country} isAdmin={isAdmin}
        onSaved={saved => { setFormInvoice(undefined); refresh(); setDetailId(saved.id); }} onOpenExisting={openExisting} />
      <PurchaseUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} country={country} isAdmin={isAdmin} onImported={refresh} />
      <PurchaseDetailDialog purchaseId={detailId} onOpenChange={open => !open && setDetailId(null)} country={country} isAdmin={isAdmin} canEdit={canEdit}
        onChanged={refresh} onEdit={invoice => { setDetailId(null); setFormInvoice(invoice); }} />
    </div>
  );
}
